/**
 * G11. Смена, по типу работы: поле — собрать спелые клетки (сетка 3×3),
 * завод — конвейер в ритм (6 ударов), офис — 6 карточек по двум стопкам,
 * торговля — сдача (3 суммы). Пороги точности написаны.
 */
import { t } from '@life/i18n';
import { SHIFT_PROMOTION_BONUS, SHIFT_SHARP_ACCURACY, SHIFT_TIRED_ACCURACY, SHIFT_TIRED_HEALTH, type ShiftInput, type ShiftState } from '@life/minigames';
import { useState } from 'preact/hooks';
import type { MinigameScreenProps } from './types';
import { Choice } from './ui/Choice';
import { Pick } from './ui/Pick';
import { Tap } from './ui/Tap';

function Rules({ state }: { state: ShiftState }) {
  return (
    <p class="hint">
      {t('ui.minigame.shift.rules', {
        kind: t(`minigame.shift.kind.${state.kind}`),
        sharp: Math.round(SHIFT_SHARP_ACCURACY * 100),
        bonus: Math.round(SHIFT_PROMOTION_BONUS * 100),
        tired: Math.round(SHIFT_TIRED_ACCURACY * 100),
        h: SHIFT_TIRED_HEALTH,
      })}
    </p>
  );
}

function FieldShift({ state, onInput }: MinigameScreenProps<ShiftInput, ShiftState>) {
  const [picks, setPicks] = useState<number[]>([]);
  return (
    <>
      <p class="hint">{t('ui.minigame.shift.field.rules')}</p>
      <Pick
        grid
        quiet
        label={t('minigame.shift.kind.field')}
        items={state.cells.map((ripe) => ({ text: t(ripe ? 'ui.minigame.shift.ripe' : 'ui.minigame.shift.green') }))}
        max={state.cells.length}
        value={picks}
        onChange={setPicks}
      />
      <button type="button" class="btn accent block" disabled={picks.length === 0} onClick={() => onInput({ picks })}>
        {t('ui.minigame.shift.field.done')}
      </button>
    </>
  );
}

function OfficeShift({ state, onInput }: MinigameScreenProps<ShiftInput, ShiftState>) {
  const [picks, setPicks] = useState<(number | null)[]>(() => state.cards.map(() => null));
  const complete = picks.every((p) => p !== null);
  return (
    <>
      <p class="hint">{t('ui.minigame.shift.office.rules')}</p>
      {state.cards.map((card, i) => (
        <div class="q row" key={i} data-question={i}>
          <p class="q-text">{t(card.key)}</p>
          <Choice label={t(card.key)} items={state.stacks.map((s) => ({ text: t(s) }))} value={picks[i] ?? null} onChange={(j) => setPicks(picks.map((p, k) => (k === i ? j : p)))} />
        </div>
      ))}
      <button type="button" class="btn accent block" disabled={!complete} onClick={() => onInput({ picks: picks.map((p) => p ?? -1) })}>
        {t('ui.minigame.shift.office.done')}
      </button>
    </>
  );
}

function TradeShift({ state, onInput }: MinigameScreenProps<ShiftInput, ShiftState>) {
  const [picks, setPicks] = useState<(number | null)[]>(() => state.sums.map(() => null));
  const complete = picks.every((p) => p !== null);
  return (
    <>
      <p class="hint">{t('ui.minigame.shift.trade.rules')}</p>
      {state.sums.map((sum, i) => (
        <div class="q" key={i} data-question={i}>
          <p class="q-text">{t('ui.minigame.shift.trade.sum', { price: sum.price, paid: sum.paid })}</p>
          <Choice label={t('ui.minigame.shift.trade.sum', { price: sum.price, paid: sum.paid })} items={sum.options.map((o) => ({ text: String(o) }))} value={picks[i] ?? null} onChange={(j) => setPicks(picks.map((p, k) => (k === i ? j : p)))} />
        </div>
      ))}
      <button type="button" class="btn accent block" disabled={!complete} onClick={() => onInput({ picks: picks.map((p) => p ?? -1) })}>
        {t('ui.minigame.shift.trade.done')}
      </button>
    </>
  );
}

export function Shift(props: MinigameScreenProps<ShiftInput, ShiftState>) {
  const { state, onInput } = props;
  let body;
  switch (state.kind) {
    case 'field':
      body = <FieldShift {...props} />;
      break;
    case 'factory':
      body = (
        <>
          <p class="hint">{t('ui.minigame.shift.factory.rules', { beats: state.beats.length, s: state.durationMs / 1000 })}</p>
          <Tap beats={state.beats} durationMs={state.durationMs} onDone={(taps) => onInput({ taps })} />
        </>
      );
      break;
    case 'office':
      body = <OfficeShift {...props} />;
      break;
    case 'trade':
      body = <TradeShift {...props} />;
      break;
  }
  return (
    <>
      <Rules state={state} />
      {body}
    </>
  );
}
